import type { FlagOverrideMap, FlagOverrideValue } from './overrides';

/**
 * The PostHog flags a desktop e2e build boots with overridden.
 *
 * The web e2e lane pins these by intercepting PostHog's `/flags/` request
 * (`DEFAULT_POSTHOG_OVERRIDES` in `@heidi/e2e-utils`). The desktop native lane
 * drives the OS accessibility tree and cannot intercept, so the e2e build
 * bakes that same list into `NEXT_PUBLIC_E2E_FLAG_OVERRIDES`
 * (`scripts/lib/e2e-flag-overrides.ts`) and the dev-tools override store
 * starts from it. One list, both lanes.
 *
 * Empty unless this is a non-production desktop e2e build. That is a RUNTIME
 * refusal, not dead-code elimination: the values arrive through the `env`
 * parameter (so tests can pass their own), which the bundler cannot fold the
 * way it folds a direct `process.env.NEXT_PUBLIC_ENVIRONMENT` comparison like
 * `scribeHttpErrorHandler.ts`'s. So this body ships in production bundles and
 * returns `{}` there — and the publish workflows never set either variable, so
 * a shipped build has no list to read in any case.
 */
export const readE2eFlagDefaults = (
  env: {
    NEXT_PUBLIC_ENVIRONMENT?: string;
    NEXT_PUBLIC_TAURI_E2E?: string;
    NEXT_PUBLIC_E2E_FLAG_OVERRIDES?: string;
  } = {
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
    NEXT_PUBLIC_TAURI_E2E: process.env.NEXT_PUBLIC_TAURI_E2E,
    NEXT_PUBLIC_E2E_FLAG_OVERRIDES: process.env.NEXT_PUBLIC_E2E_FLAG_OVERRIDES,
  }
): FlagOverrideMap => {
  if (env.NEXT_PUBLIC_ENVIRONMENT === 'production') return {};
  if (env.NEXT_PUBLIC_TAURI_E2E !== 'true') return {};
  const raw = env.NEXT_PUBLIC_E2E_FLAG_OVERRIDES;
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn('[flags-dev] NEXT_PUBLIC_E2E_FLAG_OVERRIDES is not JSON; ignoring it');
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.warn(
      '[flags-dev] NEXT_PUBLIC_E2E_FLAG_OVERRIDES is not an object; ignoring it'
    );
    return {};
  }

  const overrides: Record<string, FlagOverrideValue> = {};
  for (const [flagKey, value] of Object.entries(parsed)) {
    if (value === true) overrides[`posthog:${flagKey}`] = 'on';
    else if (value === false) overrides[`posthog:${flagKey}`] = 'off';
    else if (typeof value === 'string' && value) {
      overrides[`posthog:${flagKey}`] = `variant:${value}`;
    }
  }
  // Counts only, never keys or values: a partial list is as quiet a failure as
  // an empty one, and this line is what tells the two apart in the app log.
  const dropped = Object.keys(parsed).length - Object.keys(overrides).length;
  if (dropped > 0) {
    console.warn(
      `[flags-dev] NEXT_PUBLIC_E2E_FLAG_OVERRIDES: ignored ${dropped} entr${dropped === 1 ? 'y' : 'ies'} that were not a boolean or a variant`
    );
  }
  return overrides;
};

/**
 * The pinned defaults with whatever is stored laid on top, key by key.
 *
 * A stored map does not replace the defaults wholesale: a spec that pins one
 * flag through the dev-tools panel (`installDevToolsFlagOverrides`) stores only
 * that key, and every other pin must survive it. Where both name a flag, the
 * stored choice wins, so a spec can still opt a pinned flag back in.
 */
export const layerOverDefaults = (
  stored: FlagOverrideMap,
  defaults: FlagOverrideMap
): FlagOverrideMap =>
  Object.keys(defaults).length === 0 ? stored : { ...defaults, ...stored };
